import { useEffect, useRef, useState } from 'react';

/**
 * How long a skeleton stays invisible before it appears. A read that lands
 * inside this window renders straight in, so a fast network never flashes a
 * skeleton. `SkeletonRegion` applies the same delay in CSS.
 */
export const SKELETON_DELAY_MS = 200;

/** Once a skeleton is visible it stays at least this long, so it never blinks on and off. */
export const SKELETON_MIN_VISIBLE_MS = 300;

/**
 * How long a group waits for its slowest read before revealing what has
 * arrived. Long enough that typical sibling reads land together, short enough
 * that one slow read cannot hold the rest of the group hostage. Must exceed
 * SKELETON_DELAY_MS + SKELETON_MIN_VISIBLE_MS, or the cap would cut the hold short.
 */
export const REVEAL_GROUP_CAP_MS = 800;

/** The slice of a TanStack query result the group reads. A disabled query is not loading, so it never holds the group. */
export interface RevealRead {
  isLoading: boolean;
}

export interface RevealGroup {
  /** True once the group may show its content. Latched until the route path changes: a later refetch never re-hides the group. */
  revealed: boolean;
  /** True when `read`'s content should render: the group is revealed and this read has settled. */
  ready: (read: RevealRead) => boolean;
  /** `animate-reveal` only if a skeleton was actually visible, so fast and cached loads render without a fade. */
  revealClass: string;
}

/**
 * Reveals a group of sibling reads (a figure row, a summary card, a page's
 * list) as one, instead of letting each pop in as it lands.
 *
 * Settled means "not `isLoading`": data, an error, or a disabled query. The
 * group reveals once every read has settled or the cap has passed, but never
 * while a skeleton that became visible is inside its minimum hold. Errors are
 * settled, so a failed read renders its own `ReadError` inside the revealed
 * group rather than blocking its siblings. Render each read's skeleton until
 * `ready(read)`.
 */
export function useRevealGroup(reads: readonly RevealRead[], capMs = REVEAL_GROUP_CAP_MS): RevealGroup {
  const settled = reads.every((read) => !read.isLoading);
  const [skeletonVisible, setSkeletonVisible] = useState(false);
  const [holdElapsed, setHoldElapsed] = useState(false);
  const [capPassed, setCapPassed] = useState(false);
  const revealedRef = useRef(false);

  // A new route path is a new load (a detail page reused for the next record),
  // so the latch and the timers start again. A query-string change (a filter, a
  // sort) is not: those lists keep their previous rows on screen. Read from the
  // browser rather than the router, so a group in a dialog or listbox rendered
  // outside a router still works; a navigation has updated it by the time the
  // page re-renders.
  const { pathname } = window.location;
  const [cyclePath, setCyclePath] = useState(pathname);
  if (cyclePath !== pathname) {
    setCyclePath(pathname);
    setSkeletonVisible(false);
    setHoldElapsed(false);
    setCapPassed(false);
    revealedRef.current = false;
  }

  // Read the latch before writing it, as useLatchedLoadPending does.
  const revealed = revealedRef.current
    || ((settled || capPassed) && (!skeletonVisible || holdElapsed));
  if (revealed) revealedRef.current = true;

  useEffect(() => {
    if (revealed) return;
    const showTimer = setTimeout(() => setSkeletonVisible(true), SKELETON_DELAY_MS);
    const holdTimer = setTimeout(() => setHoldElapsed(true), SKELETON_DELAY_MS + SKELETON_MIN_VISIBLE_MS);
    const capTimer = setTimeout(() => setCapPassed(true), capMs);
    return () => {
      clearTimeout(showTimer);
      clearTimeout(holdTimer);
      clearTimeout(capTimer);
    };
  }, [revealed, capMs, cyclePath]);

  return {
    revealed,
    ready: (read) => revealed && !read.isLoading,
    revealClass: skeletonVisible ? 'animate-reveal' : '',
  };
}
